import React, { useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  CircularProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Chip,
  Divider,
  Grid,
} from '@mui/material';
import {
  Close as CloseIcon,
  CameraAlt as CameraIcon,
  FlipCameraAndroid as FlipIcon,
  Refresh as RefreshIcon,
  Check as CheckIcon,
  CloudUpload as UploadIcon,
  Person as PersonIcon,
  Badge as BadgeIcon,
} from '@mui/icons-material';
import Webcam from 'react-webcam';
import { ocrSpaceDocumentAPI, parseDocumentOCR } from '../../services/ocrService';
import { subClientService } from '../../services/api';

// Keys must match SubClientDocument::DOCUMENT_TYPES on the backend
const DOCUMENT_TYPES = [
  { value: 'identity_card', label: "Carte d'identité" },
  { value: 'passport', label: 'Passeport' },
  { value: 'driving_license', label: 'Permis de conduire' },
  { value: 'other', label: 'Autre' },
];
const DOCUMENT_TYPE_LABELS: Record<string, string> = DOCUMENT_TYPES.reduce((acc, d) => {
  acc[d.value] = d.label;
  return acc;
}, {} as Record<string, string>);

interface Props {
  open: boolean;
  onClose: () => void;
  /** When provided: attach document to this existing client */
  existingClientId?: number;
  existingClientName?: string;
  /** When not provided: new client creation mode */
  onClientCreated?: (clientId: number) => void;
  onDocumentAttached?: () => void;
}

const IDScannerModal: React.FC<Props> = ({
  open,
  onClose,
  existingClientId,
  existingClientName,
  onClientCreated,
  onDocumentAttached,
}) => {
  const isNewClient = !existingClientId;
  const steps = isNewClient
    ? ['Capture', 'Vérifier données', 'Confirmer']
    : ['Capturer / Importer', 'Confirmer'];

  const webcamRef = useRef<Webcam>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [activeStep, setActiveStep] = useState(0);
  const [capturedImage, setCapturedImage] = useState<string>('');
  const [capturedBlob, setCapturedBlob] = useState<File | null>(null);
  const [processing, setProcessing] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');
  const [ocrError, setOcrError] = useState('');
  // Auto-detected document type (keys match backend SubClientDocument::DOCUMENT_TYPES)
  const [docType, setDocType] = useState('other');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saved, setSaved] = useState(false);

  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    phone: '',
    date_naissance: '',
    lieu_naissance: '',
    numero_id: '',
    nationality: '',
    expiry_date: '',
    address: '',
    city: '',
    postal_code: '',
    country: 'France',
  });

  const resetAll = () => {
    setActiveStep(0);
    setCapturedImage('');
    setCapturedBlob(null);
    setProcessing(false);
    setCameraReady(false);
    setCameraError('');
    setOcrError('');
    setDocType('other');
    setSaving(false);
    setSaveError('');
    setSaved(false);
    setFormData({
      nom: '', prenom: '', email: '', phone: '', date_naissance: '', lieu_naissance: '',
      numero_id: '', nationality: '', expiry_date: '', address: '', city: '', postal_code: '', country: 'France',
    });
  };

  const resetExtractedFields = () => {
    setDocType('other');
    setOcrError('');
    if (isNewClient) {
      setFormData((prev) => ({
        ...prev,
        nom: '',
        prenom: '',
        date_naissance: '',
        lieu_naissance: '',
        numero_id: '',
        nationality: '',
        expiry_date: '',
      }));
    }
  };

  const handleClose = () => { resetAll(); onClose(); };

  const captureFromCamera = async () => {
    if (!webcamRef.current) return;
    const imgSrc = webcamRef.current.getScreenshot({ width: 1920, height: 1080 });
    if (!imgSrc) return;
    processCapture(imgSrc, imgSrc);
  };

  const base64ToFile = (base64: string, filename: string): File => {
    const arr = base64.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
    const bstr = atob(arr[1]);
    const u8arr = new Uint8Array(bstr.length);
    for (let i = 0; i < bstr.length; i++) u8arr[i] = bstr.charCodeAt(i);
    return new File([u8arr], filename, { type: mime });
  };

  /**
   * Resize + JPEG-compress a base64 image so it stays under OCR.space's 1 MB limit.
   * Shrinks proportionally to MAX_DIM on the longest side, then re-encodes at QUALITY.
   */
  const compressImage = (base64: string): Promise<string> => {
    const MAX_DIM = 1600;
    const QUALITY = 0.80;
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > MAX_DIM || height > MAX_DIM) {
          if (width >= height) { height = Math.round((height * MAX_DIM) / width); width = MAX_DIM; }
          else { width = Math.round((width * MAX_DIM) / height); height = MAX_DIM; }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d')!;
        ctx.drawImage(img, 0, 0, width, height);
        const compressed = canvas.toDataURL('image/jpeg', QUALITY);
        console.log(`[OCR] Image compressed: ${(base64.length / 1024).toFixed(0)} KB → ${(compressed.length / 1024).toFixed(0)} KB (${width}×${height})`);
        resolve(compressed);
      };
      img.src = base64;
    });
  };

  const processCapture = async (imgSrc: string, b64: string) => {
    // Every new scan starts clean to avoid showing stale OCR data on failure.
    resetExtractedFields();

    // Compress before doing anything — OCR.space free tier cap is 1 MB
    const compressed = await compressImage(b64);
    setCapturedImage(compressed);
    const file = base64ToFile(compressed, 'id-scan.jpg');
    setCapturedBlob(file);

    setProcessing(true);
    setOcrError('');
    try {
      const result = await ocrSpaceDocumentAPI(compressed);
      if (result.text) {
        // Auto-detection only: no manual type hint
        const parsed = parseDocumentOCR(result.text);
        if (parsed.detected_doc_type) {
          setDocType(parsed.detected_doc_type);
        }
        if (isNewClient) {
          setFormData((prev) => ({
            ...prev,
            nom: parsed.nom,
            prenom: parsed.prenom,
            date_naissance: parsed.date_naissance,
            lieu_naissance: parsed.lieu_naissance,
            numero_id: parsed.numero_id,
            nationality: parsed.nationality,
            expiry_date: parsed.expiry_date,
          }));
        }
      } else {
        setOcrError('Aucun texte détecté sur ce scan. Veuillez réessayer.');
      }
      setActiveStep(1);
    } catch {
      setOcrError('OCR indisponible — vous pourrez quand même enregistrer le document.');
      setActiveStep(1);
    } finally {
      setProcessing(false);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCapturedBlob(file);
    const reader = new FileReader();
    reader.onload = () => {
      const b64 = reader.result as string;
      processCapture(b64, b64);
    };
    reader.readAsDataURL(file);
    // Reset so the same file can be picked again
    e.target.value = '';
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError('');
    try {
      if (isNewClient) {
        const payload = {
          nom: formData.nom,
          prenom: formData.prenom,
          name: `${formData.prenom} ${formData.nom}`.trim(),
          email: formData.email || undefined,
          phone: formData.phone || undefined,
          date_naissance: formData.date_naissance || undefined,
          lieu_naissance: formData.lieu_naissance || undefined,
          address: formData.address || undefined,
          city: formData.city || undefined,
          postal_code: formData.postal_code || undefined,
          country: formData.country || undefined,
          type: 'individual' as const,
          is_active: true,
        };

        const res = await subClientService.create(payload);
        const newId: number = res.data.data?.id as number;

        if (capturedBlob && newId) {
          await subClientService.uploadDocument(newId, capturedBlob, docType);
        }

        setSaved(true);
        onClientCreated?.(newId);
      } else {
        if (capturedBlob && existingClientId) {
          await subClientService.uploadDocument(existingClientId, capturedBlob, docType);
        }
        setSaved(true);
        onDocumentAttached?.();
      }
    } catch (err: any) {
      setSaveError(err.response?.data?.message || 'Erreur lors de la sauvegarde.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
    >
      {/* Header */}
      <Box sx={{ bgcolor: '#1565c0', color: 'white', px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <BadgeIcon />
          <Box>
            <Typography variant="h6" fontWeight={700}>
              {isNewClient ? "Scanner pièce d'identité — Nouveau client" : `Scanner document — ${existingClientName || 'Client'}`}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>
              {isNewClient ? 'Scan OCR pour pré-remplir la fiche client' : 'Ajouter un document au dossier client'}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={handleClose} sx={{ color: 'white' }}><CloseIcon /></IconButton>
      </Box>

      {/* Stepper */}
      <Box sx={{ px: 3, pt: 2.5, pb: 1 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <DialogContent sx={{ pt: 1, pb: 3, minHeight: 400 }}>

        {/* ── Step 0: Camera / Upload ────────────────────────────────────────── */}
        {activeStep === 0 && (
          <Box>
            {processing ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6, gap: 2 }}>
                <CircularProgress size={48} />
                <Typography color="text.secondary">Analyse OCR en cours…</Typography>
              </Box>
            ) : (
              <>
                <Box sx={{ border: '2px dashed #1565c0', borderRadius: 3, overflow: 'hidden', mb: 2 }}>
                  {cameraError ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                      <Typography color="error" variant="body2">{cameraError}</Typography>
                      <Button startIcon={<RefreshIcon />} onClick={() => setCameraError('')} sx={{ mt: 1 }}>Réessayer</Button>
                    </Box>
                  ) : (
                    <Box sx={{ position: 'relative', width: '100%', aspectRatio: '4/3', bgcolor: 'black' }}>
                      <Webcam
                        ref={webcamRef}
                        audio={false}
                        screenshotFormat="image/jpeg"
                        videoConstraints={{ facingMode, width: { ideal: 1920 }, height: { ideal: 1080 } }}
                        onUserMedia={() => setCameraReady(true)}
                        onUserMediaError={() => setCameraError('Caméra inaccessible — utilisez le bouton "Importer" ci-dessous.')}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        forceScreenshotSourceSize
                      />
                      {cameraReady && (
                        <Box sx={{
                          position: 'absolute', top: '15%', left: '10%', width: '80%', height: '70%',
                          border: '2px solid rgba(255,255,255,0.7)', borderRadius: 2,
                          boxShadow: '0 0 0 9999px rgba(0,0,0,0.4)', pointerEvents: 'none',
                        }}>
                          <Typography sx={{ position: 'absolute', top: -22, left: 0, color: 'white', fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
                            Centrez le document dans le cadre
                          </Typography>
                        </Box>
                      )}
                      <IconButton
                        onClick={() => setFacingMode((f) => f === 'user' ? 'environment' : 'user')}
                        sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'rgba(0,0,0,0.5)', color: 'white', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}
                      >
                        <FlipIcon />
                      </IconButton>
                      {cameraReady && (
                        <Box sx={{ position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)' }}>
                          <Button
                            variant="contained"
                            size="large"
                            startIcon={<CameraIcon />}
                            onClick={captureFromCamera}
                            sx={{ borderRadius: '50px', px: 4, fontWeight: 700, bgcolor: '#1565c0', boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }}
                          >
                            Capturer
                          </Button>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>

                <Divider sx={{ my: 2 }}><Chip label="ou" size="small" /></Divider>

                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<UploadIcon />}
                    onClick={() => fileInputRef.current?.click()}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                  >
                    Importer depuis la galerie
                  </Button>
                  <input ref={fileInputRef} type="file" hidden accept="image/*" onChange={handleFileUpload} />
                </Box>
                {ocrError && <Alert severity="warning" sx={{ mt: 2 }}>{ocrError}</Alert>}
              </>
            )}
          </Box>
        )}

        {/* ── Step 1 (new client): Review & edit extracted data ─────────────── */}
        {activeStep === 1 && isNewClient && (
          <Box>
            <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
              {capturedImage && (
                <Box sx={{ flexShrink: 0, width: 180 }}>
                  <Box component="img" src={capturedImage} sx={{ width: '100%', borderRadius: 2, border: '1px solid #e0e4ea', objectFit: 'contain' }} />
                  <Button size="small" startIcon={<RefreshIcon />} onClick={() => { setCapturedImage(''); setCapturedBlob(null); setActiveStep(0); }} sx={{ mt: 0.5, textTransform: 'none', fontSize: '0.72rem' }}>
                    Rescanner
                  </Button>
                </Box>
              )}
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <PersonIcon sx={{ color: '#1565c0' }} />
                  <Typography variant="subtitle1" fontWeight={700} color="#1565c0">Données extraites — vérifiez et complétez</Typography>
                </Box>
                {ocrError && <Alert severity="warning" sx={{ mb: 2 }}>{ocrError}</Alert>}
                <Grid container spacing={1.5}>
                  <Grid size={{ xs: 6 }}>
                    <TextField size="small" fullWidth label="Nom *" value={formData.nom} onChange={(e) => setFormData((f) => ({ ...f, nom: e.target.value }))} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <TextField size="small" fullWidth label="Prénom *" value={formData.prenom} onChange={(e) => setFormData((f) => ({ ...f, prenom: e.target.value }))} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <TextField size="small" fullWidth label="Date de naissance" type="date" value={formData.date_naissance} onChange={(e) => setFormData((f) => ({ ...f, date_naissance: e.target.value }))} InputLabelProps={{ shrink: true }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <TextField size="small" fullWidth label="Lieu de naissance" value={formData.lieu_naissance} onChange={(e) => setFormData((f) => ({ ...f, lieu_naissance: e.target.value }))} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <TextField size="small" fullWidth label="N° pièce d'identité" value={formData.numero_id} onChange={(e) => setFormData((f) => ({ ...f, numero_id: e.target.value }))} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <TextField size="small" fullWidth label="Nationalité" value={formData.nationality} onChange={(e) => setFormData((f) => ({ ...f, nationality: e.target.value }))} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <TextField size="small" fullWidth label="Date d'expiration" type="date" value={formData.expiry_date} onChange={(e) => setFormData((f) => ({ ...f, expiry_date: e.target.value }))} InputLabelProps={{ shrink: true }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <TextField size="small" fullWidth label="Email" value={formData.email} onChange={(e) => setFormData((f) => ({ ...f, email: e.target.value }))} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <TextField size="small" fullWidth label="Téléphone" value={formData.phone} onChange={(e) => setFormData((f) => ({ ...f, phone: e.target.value }))} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <TextField size="small" fullWidth label="Ville" value={formData.city} onChange={(e) => setFormData((f) => ({ ...f, city: e.target.value }))} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                  </Grid>
                </Grid>
              </Box>
            </Box>

            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip
                color="primary"
                variant="outlined"
                label={`Type détecté: ${DOCUMENT_TYPE_LABELS[docType] || 'Autre'}`}
              />
              {capturedBlob && (
                <Typography variant="caption" color="text.secondary">
                  {capturedBlob.name} ({(capturedBlob.size / 1024).toFixed(0)} ko)
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {/* ── Step 1 (existing client): Choose document type ────────────────── */}
        {activeStep === 1 && !isNewClient && (
          <Box>
            <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
              {capturedImage && (
                <Box sx={{ flexShrink: 0, width: 240 }}>
                  <Box component="img" src={capturedImage} sx={{ width: '100%', borderRadius: 2, border: '1px solid #e0e4ea', objectFit: 'contain' }} />
                  <Button size="small" startIcon={<RefreshIcon />} onClick={() => { setCapturedImage(''); setCapturedBlob(null); setActiveStep(0); }} sx={{ mt: 0.5, textTransform: 'none', fontSize: '0.72rem' }}>
                    Rescanner
                  </Button>
                </Box>
              )}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 2 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                  Document capturé pour <em>{existingClientName}</em>
                </Typography>
                <Chip
                  color="primary"
                  variant="outlined"
                  sx={{ alignSelf: 'flex-start' }}
                  label={`Type détecté: ${DOCUMENT_TYPE_LABELS[docType] || 'Autre'}`}
                />
                {ocrError && <Alert severity="warning">{ocrError}</Alert>}
                {capturedBlob && (
                  <Typography variant="caption" color="text.secondary">
                    Fichier : {capturedBlob.name} ({(capturedBlob.size / 1024).toFixed(0)} ko)
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        )}

        {/* ── Saved confirmation ─────────────────────────────────────────────── */}
        {saved && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CheckIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" fontWeight={700} color="success.main">
              {isNewClient ? 'Client créé avec succès !' : 'Document ajouté au dossier !'}
            </Typography>
            <Button onClick={handleClose} sx={{ mt: 2, borderRadius: 2, textTransform: 'none' }} variant="contained" color="success">
              Fermer
            </Button>
          </Box>
        )}

        {saveError && <Alert severity="error" sx={{ mt: 2 }}>{saveError}</Alert>}
      </DialogContent>

      {/* Footer actions */}
      {!saved && activeStep > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 3, pb: 3, gap: 2 }}>
          <Button onClick={() => setActiveStep((s) => s - 1)} sx={{ borderRadius: 2, textTransform: 'none' }}>
            Retour
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !capturedBlob || (isNewClient && (!formData.nom.trim() && !formData.prenom.trim()))}
            startIcon={saving ? <CircularProgress size={18} sx={{ color: 'white' }} /> : <CheckIcon />}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, bgcolor: '#1565c0', '&:hover': { bgcolor: '#0d47a1' }, px: 3 }}
          >
            {isNewClient ? 'Créer le client' : 'Enregistrer le document'}
          </Button>
        </Box>
      )}
    </Dialog>
  );
};

export default IDScannerModal;
